// Section 17 shortage dialog row: name, age, job, strength, seasons short in the last four,
// household, the share as a bar (full, partial, none, spelled out too), inline full / half /
// none controls and a pin, plus a one-line consequence hint shown on hover or focus. Props only.
import type { JobId, PersonId } from '../../sim/api';
import type { ShortageRow as Row } from '../../viewModels/shortage';
import { rationsText, shortageText } from '../text/en';

interface Props {
  row: Row;
  /** The share shown in the bar: the player's setting, or what the policy allots. */
  share: number;
  edited: boolean;
  pinned: boolean;
  hint: string;
  canPin: boolean;
  jobLabel: (job: JobId) => string;
  onSet: (id: PersonId, share: number) => void;
  onPin: (id: PersonId, pinned: boolean) => void;
}

const CHOICES = [
  { share: 1, label: shortageText.full },
  { share: 0.5, label: shortageText.half },
  { share: 0, label: shortageText.noneButton },
] as const;

export function ShortageRow({
  row,
  share,
  edited,
  pinned,
  hint,
  canPin,
  jobLabel,
  onSet,
  onPin,
}: Props) {
  const hintId = `shortage-hint-${row.id}`;
  const level = share >= 1 - 1e-9 ? 'full' : share <= 1e-9 ? 'none' : 'partial';
  return (
    <tr class={`shortage__row is-${level}${edited ? ' is-edited' : ''}`}>
      <th scope="row" class="name">
        {row.name}
        {edited && <span class="shortage__edited"> ({shortageText.edited})</span>}
        <span class="shortage__hint" id={hintId}>
          {hint}
        </span>
      </th>
      <td class="num">{row.ageYears}</td>
      <td>{jobLabel(row.job)}</td>
      <td class="num">{Math.round(row.strength)}</td>
      <td class="num">{row.seasonsShort}</td>
      <td>{rationsText.householdName(row.householdName)}</td>
      <td class="shortage__share">
        <span class="shortage__bar" aria-hidden="true">
          <span class="shortage__fill" style={{ '--fill': `${Math.round(share * 100)}%` }} />
        </span>
        <span class="num">{shortageText.share(share)}</span>
      </td>
      <td>
        <span class="shortage__choices" role="group" aria-label={shortageText.colSet}>
          {CHOICES.map((c) => {
            const on = edited && Math.abs(share - c.share) < 1e-9;
            return (
              <button
                key={c.share}
                type="button"
                class={`button button--small${on ? ' is-selected' : ''}`}
                aria-pressed={on}
                aria-label={shortageText.setFor(c.label, row.name)}
                aria-describedby={hintId}
                onClick={() => {
                  onSet(row.id, c.share);
                }}
              >
                {c.label}
              </button>
            );
          })}
        </span>
      </td>
      <td>
        <input
          type="checkbox"
          aria-label={shortageText.pinFor(row.name)}
          aria-describedby={hintId}
          checked={pinned}
          disabled={!canPin}
          onChange={(e) => {
            onPin(row.id, e.currentTarget.checked);
          }}
        />
      </td>
    </tr>
  );
}
